/**
 * Composable for fetching and transforming AI Tools data from Sanity via cached server APIs
 */
import { withSsrDependencyTiming } from '@/lib/ssr-timing';
import type { SiteLocale } from '@/lib/site-locale';
import type { ToolCategoryValue } from '@/lib/tool-taxonomy';

export interface AiToolListResponse {
  _id?: string;
  _updatedAt?: string;
  slug?: string;
  language?: SiteLocale;
  translationKey?: string;
  sceneKeywords?: string;
  heroTitle?: string;
  subtitle?: string;
  category?: ToolCategoryValue;
  pageFamily?: string;
  image?: string;
}

interface AiToolContentItem {
  title?: string;
  description?: string;
  image?: string;
  imageAlt?: string;
}

interface AiToolGenerationMethod {
  title?: string;
  description?: string;
  features?: string[];
  cta?: string;
}

interface AiToolTestimonial {
  content?: string;
  author?: string;
  role?: string;
  avatar?: string;
  avatarAlt?: string;
}

export interface AiToolDetailResponse {
  _id?: string;
  _updatedAt?: string;
  slug?: string;
  language?: SiteLocale;
  translationKey?: string;
  alternates?: Array<{ language?: SiteLocale; slug?: string }>;
  seoTitle?: string;
  seoDescription?: string;
  keywords?: string[];
  ogImage?: string;
  ogImageAlt?: string;
  subtitle?: string;
  category?: string;
  sceneKeywords?: string;
  heroImage?: string;
  heroImageAlt?: string;
  heroProductType?: string;
  heroTitle?: string;
  tagline?: Array<{ tagline?: string; data?: string }>;
  chooseReasons?: AiToolContentItem[];
  chooseReasonsProductType?: string;
  chooseReasonsSectionCta?: string;
  generationMethods?: AiToolGenerationMethod[];
  generationMethodsProductType?: string;
  howToSteps?: AiToolContentItem[];
  howToProductType?: string;
  howToSectionTitle?: string;
  useCases?: AiToolContentItem[];
  useCasesSectionTitle?: string;
  testimonials?: AiToolTestimonial[];
  testimonialsSectionTitle?: string;
  testimonialsAudienceType?: string;
  faq?: Array<{ question?: string; answer?: string }>;
  faqSectionTitle?: string;
  faqSectionDescription?: string;
  finalCtaTitle?: string;
}

export const useAiTools = () => {
  /**
   * Fetch AI Tools with Sanity status == published (list + footer)
   * Uses server-side Redis caching for improved performance
   */
  const fetchAllTools = async (
    locale: SiteLocale = 'en',
    options: { category?: ToolCategoryValue; preview?: boolean } = {}
  ): Promise<AiToolListResponse[]> => {
    const query = {
      locale,
      pageFamily: 'ai-design-tools',
      ...(options.category ? { category: options.category } : {}),
      ...(options.preview ? { preview: '1' } : {}),
    };
    try {
      return await withSsrDependencyTiming(
        'api:ai-tools:all',
        () => $fetch<AiToolListResponse[]>('/api/ai-tools/all', { query }),
        {
          url: `/api/ai-tools/all?locale=${locale}&pageFamily=ai-design-tools`,
          kind: 'internal',
        }
      );
    } catch (error) {
      console.error('Error fetching all tools from API:', error);
      return [];
    }
  };

  /**
   * Footer “Design Tools” links: curated order from Sanity singleton `footerSettings`
   */
  const fetchFooterTools = async (locale: SiteLocale = 'en'): Promise<AiToolListResponse[]> => {
    try {
      return await withSsrDependencyTiming(
        'api:ai-tools:footer',
        () => $fetch<AiToolListResponse[]>('/api/ai-tools/footer', { query: { locale } }),
        { url: `/api/ai-tools/footer?locale=${locale}`, kind: 'internal' }
      );
    } catch (error) {
      console.error('Error fetching footer tools from API:', error);
      return [];
    }
  };

  /**
   * Fetch related tools (excluding current tool)
   * Uses server-side Redis caching for improved performance
   */
  const fetchRelatedTools = async (
    excludeSlug: string,
    limit: number = 4,
    locale: SiteLocale = 'en'
  ) => {
    try {
      return await withSsrDependencyTiming(
        'api:ai-tools:related',
        () =>
          $fetch('/api/ai-tools/related', {
            query: { excludeSlug, limit, locale, pageFamily: 'ai-design-tools' },
          }),
        {
          url: `/api/ai-tools/related?locale=${locale}&pageFamily=ai-design-tools&excludeSlug=${encodeURIComponent(excludeSlug)}&limit=${limit}`,
          kind: 'internal',
        }
      );
    } catch (error) {
      console.error('Error fetching related tools from API:', error);
      return [];
    }
  };

  /**
   * Fetch a single AI Tool by slug
   * Uses server-side Redis caching for improved performance
   */
  const fetchToolBySlug = async (slug: string, locale: SiteLocale = 'en') => {
    try {
      return await withSsrDependencyTiming(
        'api:ai-tools:detail',
        () =>
          $fetch<AiToolDetailResponse>(`/api/ai-tools/${encodeURIComponent(slug)}`, {
            query: { locale, pageFamily: 'ai-design-tools' },
          }),
        {
          url: `/api/ai-tools/${encodeURIComponent(slug)}?locale=${locale}&pageFamily=ai-design-tools`,
          kind: 'internal',
        }
      );
    } catch (error) {
      console.error(`Error fetching tool by slug ${slug} from API:`, error);
      return null;
    }
  };

  return {
    fetchAllTools,
    fetchFooterTools,
    fetchRelatedTools,
    fetchToolBySlug,
  };
};
